import { useCallback, useMemo, useState } from 'react'

import { format, isValid, parseISO } from 'date-fns'

import { toPostStatuses } from '~publish/components/FilterByStatus'
import type { PostStatus } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'
import { toQueryParamArray, useQueryParam } from '~publish/hooks/useQueryParam'

export type BoardGroupBy = 'status' | 'tag' | 'channel'

const DATE_PARAM_FORMAT = 'yyyy-MM-dd'

export type BoardDateRange = { startDate: Date; endDate: Date }

export type BoardState = {
  groupBy: BoardGroupBy
  setGroupBy: (groupBy: BoardGroupBy) => void
  statuses: PostStatus[]
  setStatuses: (statuses: string[]) => void
  channelIds: string[]
  /** `replace: true` corrects the URL in place, without adding a history entry. */
  setChannelIds: (channelIds: string[], options?: { replace?: boolean }) => void
  tagIds: string[]
  setTagIds: (tagIds: string[]) => void
  dateRange: BoardDateRange | undefined
  setDateRange: (startDate: Date, endDate: Date) => void
  clearDateRange: () => void
}

/**
 * Board's own filter/grouping state, kept under `board*`-prefixed URL params
 * so switching to/from List or Calendar never clobbers their own `tab` /
 * `channels` / `tags` params, and vice versa.
 *
 * @param prefillChannelId On a channel page, defaults the Channels filter to
 * that channel — a default only, editable afterward. The URL can't
 * distinguish an explicit clear from "never touched" (an empty array
 * serializes to an absent param), so the clear is tracked in local state
 * instead: once cleared, the prefill stops re-applying for the session.
 */
export function useBoardState({
  prefillChannelId,
}: {
  prefillChannelId?: string
} = {}): BoardState {
  const [groupByParam, setGroupByParam] =
    useQueryParam<BoardGroupBy>('boardGroupBy')
  const [rawStatusParam, setStatusParam] =
    useQueryParam<string[]>('boardStatus')
  const statusParam = toQueryParamArray(rawStatusParam)
  const [channelsParam, setChannelsParam] =
    useQueryParam<string[]>('boardChannels')
  const [rawTagsParam, setTagsParam] = useQueryParam<string[]>('boardTags')
  const tagsParam = toQueryParamArray(rawTagsParam)
  // A single param, not separate `boardFrom`/`boardTo` ones: two setters
  // called back-to-back each close over the same pre-update `location.search`
  // (useQueryParam recomputes it from a fresh useLocation() call, one per
  // hook instance), so the second call's URL is built without the first
  // one's change and silently drops it.
  const [rawDateRangeParam, setDateRangeParam] =
    useQueryParam<string[]>('boardDateRange')
  const dateRangeParam = toQueryParamArray(rawDateRangeParam)

  const groupBy: BoardGroupBy =
    groupByParam === 'tag' || groupByParam === 'channel'
      ? groupByParam
      : 'status'

  // `boardChannels=` (empty) is indistinguishable from "absent" once
  // serialized — query-string drops empty-array params entirely — so an
  // explicit clear can't be told apart from "never touched" through the URL
  // alone. Track the clear locally instead: once the user has explicitly
  // cleared it, the prefill stops re-applying for the rest of the session.
  const [hasClearedChannelPrefill, setHasClearedChannelPrefill] =
    useState(false)

  const channelIds = useMemo(() => {
    if (channelsParam !== undefined) return toQueryParamArray(channelsParam)
    if (hasClearedChannelPrefill || !prefillChannelId) return []
    return [prefillChannelId]
  }, [channelsParam, hasClearedChannelPrefill, prefillChannelId])

  const setChannelIds = useCallback(
    (nextChannelIds: string[], options: { replace?: boolean } = {}): void => {
      if (nextChannelIds.length === 0 && prefillChannelId) {
        setHasClearedChannelPrefill(true)
      }
      setChannelsParam(nextChannelIds.length ? nextChannelIds : undefined, {
        replace: options.replace,
      })
    },
    [prefillChannelId, setChannelsParam],
  )

  const dateRange = useMemo(() => {
    const [fromParam, toParam] = dateRangeParam
    if (typeof fromParam !== 'string' || typeof toParam !== 'string') {
      return undefined
    }
    const startDate = parseISO(fromParam)
    const endDate = parseISO(toParam)
    if (!isValid(startDate) || !isValid(endDate)) return undefined
    return { startDate, endDate }
  }, [dateRangeParam])

  const setDateRange = useCallback(
    (startDate: Date, endDate: Date): void => {
      setDateRangeParam([
        format(startDate, DATE_PARAM_FORMAT),
        format(endDate, DATE_PARAM_FORMAT),
      ])
    },
    [setDateRangeParam],
  )

  const clearDateRange = useCallback((): void => {
    setDateRangeParam(undefined)
  }, [setDateRangeParam])

  return useMemo(
    () => ({
      groupBy,
      setGroupBy: setGroupByParam,
      statuses: toPostStatuses(statusParam),
      setStatuses: setStatusParam,
      channelIds,
      setChannelIds,
      tagIds: tagsParam,
      setTagIds: setTagsParam,
      dateRange,
      setDateRange,
      clearDateRange,
    }),
    [
      groupBy,
      setGroupByParam,
      statusParam,
      setStatusParam,
      channelIds,
      setChannelIds,
      tagsParam,
      setTagsParam,
      dateRange,
      setDateRange,
      clearDateRange,
    ],
  )
}
